import { runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react';
import { Component } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Route, RouteComponentProps, StaticContext, Switch, withRouter } from 'react-router';
import OrderContentLog from '../OrderContentLog/OrderContentLog';
import OrderLog from '../OrderLog/OrderLog';
import OrderTechnicalLog from '../OrderTechnicalLog/OrderTechnicalLog';
import OrderTransactionLog from '../OrderTransactionLog/OrderTransactionLog';
import OrderNotSelectedMessage from './components/OrderNotSelectedMessage/OrderNotSelectedMessage';
import OrderHeader from './containers/OrderHeader/OrderHeader';
import OrderSections from './containers/OrderSections/OrderSections';
import { withOrderStore } from './context';
import { SCREENS } from 'src/constants/screens';
import { Icons } from '~/assets/fonts/Icons/icons';
import { ORDER_STATUS } from '~/constants/order';
import { USER_ROLES } from '~/constants/user';
import { localeStore } from '~/mobx/localesStore';
import { modalStore } from '~/mobx/modalStore';
import { OrderStore } from '~/mobx/orderStore';
import { CallCenterStore, callCenterStore } from '~/mobx/ordersStore/callCenterStore';
import { LiveOrdersStore } from '~/mobx/ordersStore/liveOrdersStore';
import { userStore } from '~/mobx/userStore';
import { history } from '~/services/history';
import { TEventMessage, TOrder, TOrderCRUDEvents, orderCast } from '~/types/orders';
import { CustomEvents, subscribe, unsubscribe } from '~/utils/events';
import Icon from '~/view/components/Controls/Icon/Icon';
import Headers from '~/view/containers/Headers/Headers';
import HeaderTitle from '~/view/containers/Headers/components/HeaderTitle/HeaderTitle';
import getOrderViewPath from '~/view/screens/CheckoutScreen/getOrderViewPath';
import getOrdersPath from '~/view/screens/CheckoutScreen/getOrdersPath';

type TProps = RouteComponentProps<{ id: string }, StaticContext, TOrder | undefined> & {
  orderStore?: OrderStore;
  showScreenHeader?: boolean;
  onBack?: () => void;
};

interface IState {
  loading: boolean;
}

const callCenterStatuses = [ORDER_STATUS.DRAFT, ORDER_STATUS.ON_HOLD, ORDER_STATUS.WAITING_FOR_PAYMENT];

// @ts-ignore
@withOrderStore
@observer
class OrderScreen extends Component<TProps, IState> {
  state: IState = {
    loading: false,
  };

  get orderStore() {
    return this.props.orderStore!;
  }

  componentDidMount = () => {
    const markOrderAsViewed = this.orderStore.parentStore instanceof LiveOrdersStore;

    if (this.props.location.state && this.orderStore.order?.id !== this.props.location.state.id) {
      this.orderStore.setOrder(this.props.location.state, markOrderAsViewed);
    } else if (this.props.match.params.id) {
      this.orderStore.setOrder(this.props.match.params.id, markOrderAsViewed);
    }
    subscribe(CustomEvents.SocketUpdate, this.handleSocketEvent);
    subscribe(CustomEvents.SocketReconnect, this.handleSocketReconnect);
  };

  componentDidUpdate() {
    // Redirect RM if order status for CallCenter only
    if (
      this.orderStore.order &&
      !(this.orderStore.parentStore instanceof CallCenterStore) &&
      callCenterStatuses.includes(this.orderStore.order.order_status_code)
    ) {
      if (userStore.user.role === USER_ROLES.RESTAURANT_MANAGER) {
        history.push(getOrderViewPath(callCenterStore, this.orderStore.order.id), toJS(this.orderStore.order));
      } else {
        // Redirect other roles to OrdersList if order status for CallCenter only
        modalStore.openErrorModal({ message: localeStore.t('possible_you_dont_have_permissions_or_page_not_found') });
        history.push(getOrdersPath(this.orderStore.parentStore));
      }
    }
  }

  componentWillUnmount() {
    unsubscribe(CustomEvents.SocketUpdate, this.handleSocketEvent);
    unsubscribe(CustomEvents.SocketReconnect, this.handleSocketReconnect);
  }

  handleSocketReconnect = () => {
    this.orderStore.setOrder(this.props.match.params.id, false);
  };

  handleSocketEvent = ({ detail }: { detail: TEventMessage<TOrder, TOrderCRUDEvents> }) => {
    const { data } = detail;
    const order = orderCast(data);

    if (this.orderStore.order?.id === order.id) {
      runInAction(() => {
        order.viewed = 1;
        this.orderStore.order = order;
      });
    }
  };

  render() {
    if (this.orderStore.loading && !this.orderStore.order) {
      return (
        <div className="p-6">
          <Skeleton width={200} height={40} style={{ marginBottom: 10 }} />
        </div>
      );
    }

    if (!this.orderStore.order) {
      return <OrderNotSelectedMessage />;
    }

    return (
      <div className="d-flex flex-column px-4 full-width">
        {this.props.showScreenHeader && (
          <Headers>
            <div className="d-flex flex-row align-items-center pb-3">
              {typeof this.props.onBack === 'function' && (
                <Icon name={Icons.IcArrow} onPress={this.props.onBack} size={24} className="color-mainblue mr-4 cursor-pointer header-back__icon" />
              )}
              <HeaderTitle title="txt_view_order" />
            </div>
          </Headers>
        )}

        <OrderHeader />
        <OrderSections />

        <Switch>
          <Route
            path={[
              SCREENS.HISTORY_ORDER_LOGS(':id'),
              SCREENS.LIVE_ORDER_LOGS(':id'),
              SCREENS.CALLCENTER_ORDER_LOGS(':id'),
              SCREENS.ORDER_RATING_ORDER_LOGS(':id'),
            ]}
            component={OrderLog}
          />
          <Route
            path={[
              SCREENS.HISTORY_ORDER_TECHNICAL_LOGS(':id'),
              SCREENS.LIVE_ORDER_TECHNICAL_LOGS(':id'),
              SCREENS.CALLCENTER_ORDER_TECHNICAL_LOGS(':id'),
              SCREENS.ORDER_RATING_ORDER_TECHNICAL_LOGS(':id'),
            ]}
            component={OrderTechnicalLog}
          />
          <Route
            path={[
              SCREENS.HISTORY_ORDER_TRANSACTION_LOGS(':id'),
              SCREENS.LIVE_ORDER_TRANSACTION_LOGS(':id'),
              SCREENS.CALLCENTER_ORDER_TRANSACTION_LOGS(':id'),
              SCREENS.ORDER_RATING_ORDER_TRANSACTION_LOGS(':id'),
            ]}
            component={OrderTransactionLog}
          />
          <Route
            path={[
              SCREENS.HISTORY_ORDER_CONTENT_LOGS(':id'),
              SCREENS.LIVE_ORDER_CONTENT_LOGS(':id'),
              SCREENS.CALLCENTER_ORDER_CONTENT_LOGS(':id'),
              SCREENS.ORDER_RATING_ORDER_CONTENT_LOGS(':id'),
            ]}
            component={OrderContentLog}
          />
        </Switch>
      </div>
    );
  }
}

export default withRouter(OrderScreen);
